import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from './auth-context'
import PropTypes from 'prop-types'
import { loginRequest, msalInstance } from './msal'
import { appInsights } from 'src/AppInsights'
import { clearAllStorages, setTokensInLocalStorage } from 'src/API/Actions'
import { useDispatch, useSelector } from 'react-redux'
import { setInitialize } from 'src/redux/slices/login.slice'
import { checkUser, getDefaultRoutes } from 'src/redux/slices/Login/login.async'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { setAccessDenied } from 'src/redux/slices/route.slice'

const AuthProvider = ({ children }) => {
  const authenticated = useIsAuthenticated()
  const { accounts, instance } = useMsal()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [loginLoader, setLoginLoader] = useState(false)
  const [firstLandLocation, setFirstLandLocation] = useState(null)
  const [ready, setReady] = useState(false)
  const { allRouteList } = useSelector((state) => state.routeData)

  const { routeToNavigate } = useSelector((state) => state.login)

  const { fetchingUserLoader, isLoggedIn, defaultRoute, fetchingDefRouteLoader, initialize } =
    useSelector((state) => state.login)

  const loginHandler = useCallback(async () => {
    try {
      // Clear only this tab's sessionStorage (MSAL redirect state) before a fresh login.
      // Do NOT call clearAllStorages() — that calls localStorage.clear() which fires a
      // storage event (key === null) in every other tab, triggering cross-tab logout for
      // users who are actively logged in elsewhere.
      sessionStorage.clear()
      setLoginLoader(true)
      await msalInstance.initialize()
      await msalInstance.loginRedirect({
        ...loginRequest,
        state: firstLandLocation ? JSON.stringify(firstLandLocation) : '',
      })
    } catch (error) {
      setLoginLoader(false)
      dispatch(setInitialize(false))
    }
  }, [firstLandLocation])

  useEffect(() => {
    if (isLoggedIn && defaultRoute && authenticated && ready) {
      if (firstLandLocation) {
        navigate(firstLandLocation.pathname + firstLandLocation.search, {
          state: firstLandLocation.state,
          replace: true,
        })
        dispatch(setInitialize(false))
        // setFirstLandLocation(null)
        return
      } else if (routeToNavigate) {
        navigate(routeToNavigate)
      } else {
        navigate(defaultRoute)
      }
      dispatch(setInitialize(false))
    }
  }, [isLoggedIn, defaultRoute, firstLandLocation, ready])

  const isAuthorized = (list, path) => {
    return list.find((item) => path.includes(item))
  }

  useEffect(() => {
    instance.handleRedirectPromise().then((response) => {
      if (response) {
        if (response?.state !== '') {
          try {
            const stateData = JSON.parse(response.state)
            setFirstLandLocation(stateData)
          } catch (error) {
            console.error('Failed to parse response.state:', error)
          }
        }
        // Always mark ready once MSAL has finished processing any pending redirect.
        // In the direct-load flow (localStorage cache, no redirect), response is null
        // but we still need ready=true so the navigation effect can proceed.
        setReady(true)
      }
    })
  }, [instance])

  useEffect(() => {
    if (isLoggedIn && location) {
      const routList = allRouteList.map((item) => item.config_path)
      const isAuthorizedRoute = isAuthorized(routList, location.pathname)
      if (location.key === 'default' && !isAuthorizedRoute) {
        dispatch(setAccessDenied(true))
      } else {
        dispatch(setAccessDenied(false))
      }
    }
  }, [location, isLoggedIn, allRouteList])

  const initializeMsal = async () => {
    try {
      if (accounts.length > 0 && !isLoggedIn && !defaultRoute) {
        dispatch(setInitialize(true))
        if (!appInsights?.core?.isInitialized()) {
          appInsights?.clearAuthenticatedUserContext()
          appInsights.getCookieMgr().set('ai_user', accounts[0]?.username)
          appInsights.getCookieMgr().set('ai_session', accounts[0]?.name)
          appInsights.loadAppInsights()
        }
        msalInstance.setActiveAccount(accounts[0])
        // In MSAL v2, accounts[0].idToken is not available directly.
        // Use acquireTokenSilent to get the raw idToken from the token cache
        // so it's in localStorage before setTokensInLocalStorage makes fetch calls.
        try {
          const result = await msalInstance.acquireTokenSilent({
            scopes: loginRequest.scopes,
            account: accounts[0],
          })
          if (result?.idToken) {
            localStorage.setItem('accesstoken', result.idToken)
          }
        } catch {
          // acquireTokenSilent failed (e.g. first call before cache is warm)
          // setTokensInLocalStorage will attempt its own sessionStorage parsing
        }
        await setTokensInLocalStorage(dispatch)
        // Use token from localStorage (set by acquireTokenSilent above or by setTokensInLocalStorage)
        // accounts[0]?.idToken is not available in msal-browser v2
        const token = localStorage.getItem('accesstoken')
        if (token) {
          dispatch(checkUser({ token })).then((res) => {
            if (res.meta.requestStatus === 'fulfilled') {
              dispatch(getDefaultRoutes()).then((response)=>{
                if(response?.meta?.requestStatus === 'fulfilled'){
                  setReady(true)
                }
              })
            }
          })
          setLoginLoader(false)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }
  useEffect(() => {
    initializeMsal()
  }, [accounts])

  // Proactively refresh the AccessToken every 5 minutes to prevent silent expiry
  useEffect(() => {
    if (!isLoggedIn) return
    const refreshToken = async () => {
      const account = msalInstance.getActiveAccount()
      if (!account) return
      try {
        const result = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account,
        })
        localStorage.setItem('accesstoken', result.idToken)
      } catch {
        // Silent refresh failed — the 401 interceptor in AxiosClient will handle recovery
      }
    }
    const interval = setInterval(refreshToken, 5 * 60 * 1000) // every 5 minutes
    return () => clearInterval(interval)
  }, [isLoggedIn])

  useEffect(() => {
    if (
      location.key === 'default' &&
      !firstLandLocation &&
      !(
        location.pathname === '/' ||
        location.pathname === '/login' ||
        location.pathname?.includes('code=1.')
      )
    ) {
      setFirstLandLocation(location)
      return
    }
    // On session expiry, the app redirects to /?sessionExpired=true&redirect=<hash>
    // Restore the previous route so the existing deeplink mechanism navigates back after login
    const redirectHash = new URLSearchParams(window.location.search).get('redirect')
    if (redirectHash && !firstLandLocation) {
      const pathname = redirectHash.replace(/^#/, '') // strip leading # from hash route
      if (pathname && pathname !== '/') {
        setFirstLandLocation({ pathname, search: '', state: null })
      }
    }
  }, [])

  // Detect logout from another tab — localStorage.clear() fires storage event with key === null
  useEffect(() => {
    const handleCrossTabLogout = (event) => {
      if (event.key === null && isLoggedIn) {
        // Clear storage (empties MSAL v2 sessionStorage cache) then use SPA navigation
        // so no beforeunload dialog is triggered (unlike window.location.href)
        clearAllStorages()
        navigate('/')
      }
    }
    window.addEventListener('storage', handleCrossTabLogout)
    return () => window.removeEventListener('storage', handleCrossTabLogout)
  }, [isLoggedIn])

  const emptyCache = async () => {
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name)
        })
      })
    }
  }
  const logOutHandler = useCallback(async () => {
    try {
      await emptyCache()
      if (appInsights?.core?.isInitialized()) {
        appInsights.unload()
      }
      clearAllStorages() // sessionStorage.clear() empties MSAL v2's token cache
      navigate('/')
    } catch (error) {
      console.error(error)
    }
  }, [])
  const memoizedValue = useMemo(
    () => ({
      loginLoader: initialize || loginLoader,
      loading: initialize,
      logOutHandler,
      loginHandler,
      setLoginLoader,
    }),
    [
      loginLoader,
      logOutHandler,
      loginHandler,
      setLoginLoader,
      fetchingUserLoader,
      fetchingDefRouteLoader,
      initialize,
    ],
  )

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>
}

export default AuthProvider

AuthProvider.propTypes = {
  children: PropTypes.node,
}
